import { useMemo, useState, useEffect } from "react";
import { ICharacterPartDefinition, ISetCharacterParts } from "../../../models/interactions/SetCharacterParts";
import Interaction from "../../../models/interactions/Interaction";
import NodeActorDropdown from "../components/NodeActorDropdown";
import NodeStringDropdown from "../components/NodeStringDropdown";
import NodeInfoField from "../components/NodeInfoField";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import CharacterPartLibrary from "../../previews/set_character_part/CharacterPartLibrary";
import { CharacterPartGenderedPairDetailsDTO } from "../../../dto/CharacterPartGenderedPairDetailsDTO";
import { GetNewGuid } from "../../../helpers/GuidHelper";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCachedCharacterPartDetailsByName,
  selectCachedCharacterPartRenderByName,
  setCachedCharacterPartDetails,
  setCachedCharacterPartRender,
} from "../../../app/slices/CharacterPartsSlice";
import useSetCharacterParts from "../../previews/set_character_part/SetCharacterParts.hook";
import { CharacterPartType } from "../../../dto/CharacterPartDTO";
import { RootState } from "../../../app/Store";
import PiButton from "../../../components/PiButton";
import { selectCurrentOrganization } from "../../../app/slices/UserSlice";
import { CharacterPartService } from "../../../services/CharacterPartService";
import ClothesIcon from "../../../icons/ClothesIcon";

export default function SetCharacterPartsNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const dispatch = useDispatch();
  const setCharacterParts = props.interaction as ISetCharacterParts;
  const { updateInteraction } = useUpdateSequenceInteraction();
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  useSetCharacterParts(props.interaction); // fetches missing character part details

  const actorDropdown = useMemo(
    () => (
      <NodeActorDropdown
        label="Target"
        selectedActorId={setCharacterParts.npcId}
        onClick={(actorId: string) =>
          updateInteraction(props.nodeId, {
            ...setCharacterParts,
            npcId: actorId,
          } as ISetCharacterParts)
        }
      />
    ),
    [setCharacterParts.npcId, updateInteraction, props.nodeId]
  );

  const onAddCharacterPart = (
    characterPartDetails: CharacterPartGenderedPairDetailsDTO
  ) => {
    const definition: ICharacterPartDefinition = {
      id: GetNewGuid(),
      partId: characterPartDetails.name,
      partType: characterPartDetails.characterPartType,
      malePartId: characterPartDetails.malePartId,
      femalePartId: characterPartDetails.femalePartId,
      variantId: characterPartDetails.variants[0],
    };

    // Replace the entire array with just one part
    updateInteraction(props.nodeId, {
      ...setCharacterParts,
      partDefinitions: [definition],
    } as ISetCharacterParts);

    // Add the details to the cached char part details
    dispatch(
      setCachedCharacterPartDetails({
        characterPartName: characterPartDetails.name,
        characterPartDetails: characterPartDetails,
      })
    );
  };

  const currentPart = setCharacterParts.partDefinitions?.[0];
  const cachedPartDetails = useSelector((state: RootState) =>
    currentPart
      ? selectCachedCharacterPartDetailsByName(state, currentPart.partId)
      : null
  );
  const renderUri = useSelector((state: RootState) =>
    currentPart
      ? selectCachedCharacterPartRenderByName(state, currentPart.partId)
      : undefined
  );
  const currentOrganization = useSelector(selectCurrentOrganization);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setIsImageLoaded(false);
    setHasImageError(false);
  }, [renderUri]);

  useEffect(() => {
    if (!currentOrganization?.id || !currentPart || renderUri) return;

    const loadRender = async () => {
      const render = await CharacterPartService.GetCharacterPartGenderedPairRender(
        currentOrganization.id,
        currentPart.partId
      );
      if (!render?.downloadUri) return;
      dispatch(
        setCachedCharacterPartRender({
          characterPartName: currentPart.partId,
          downloadUri: render.downloadUri,
        })
      );
    };

    loadRender();
  }, [currentOrganization?.id, currentPart, dispatch, renderUri]);

  const renderPreview = currentPart && (
    <div className="relative flex w-full h-[120px] items-center justify-center overflow-hidden rounded-[5px] bg-interaction-softBackground">
      {renderUri && !hasImageError ? (
        <>
          {!isImageLoaded && (
            <div className="absolute inset-0 animate-pulse bg-creator-surface" />
          )}
          <img
            className="h-full w-full object-contain p-[8px]"
            src={renderUri}
            alt={currentPart.partId}
            onLoad={() => setIsImageLoaded(true)}
            onError={() => {
              setHasImageError(true);
              setIsImageLoaded(false);
            }}
          />
        </>
      ) : (
        <div className="flex items-center justify-center text-creator-text">
          <ClothesIcon size={40} />
        </div>
      )}
    </div>
  );

  const selectPartButton = (
    <div className="h-[36px] mt-[16px]">
      <PiButton variant="piBlueOutlined" onClick={() => setIsLibraryOpen(true)}>
        {currentPart ? "Change Character Pair" : "Select Character Pair"}
      </PiButton>
    </div>
  );

  const partTypeDisplay = currentPart && (
    <NodeInfoField
      label="Type"
      value={CharacterPartType[currentPart.partType]}
    />
  );

  const partIdDisplay = currentPart && (
    <NodeInfoField
      label="Pair"
      value={currentPart.partId}
    />
  );

  const variantDropdown = currentPart && cachedPartDetails && (
    <NodeStringDropdown
      label="Variant"
      items={cachedPartDetails.variants}
      selectedItem={currentPart.variantId}
      onClick={(variantId: string) => {
        updateInteraction(props.nodeId, {
          ...setCharacterParts,
          partDefinitions: [{
            ...currentPart,
            variantId,
          }],
        } as ISetCharacterParts);
      }}
    />
  );

  return (
    <div className="w-full h-full flex flex-col gap-[4px]">
      {actorDropdown}
      {partTypeDisplay}
      {partIdDisplay}
      {variantDropdown}
      {renderPreview}
      {selectPartButton}
      {isLibraryOpen && (
        <CharacterPartLibrary
          isOpen={isLibraryOpen}
          onClose={() => setIsLibraryOpen(false)}
          title="Character Part Library"
          width="calc(90vw / var(--app-zoom, 1))"
          height="calc(90vh / var(--app-zoom, 1))"
          onSubmit={onAddCharacterPart}
        />
      )}
    </div>
  );
}
